import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { api, getAuthHeaders } from '../utils/api';
import InfoTooltip from './InfoTooltip';
import { useIsMobile } from '../hooks/useIsMobile';

const assetColors = {
  homeProperty: '#8B5CF6',
  vehicles: '#EC4899',
  preciousMetals: '#F59E0B',
  electronics: '#3B82F6',
  furnitureArt: '#10B981',
  cash: '#06B6D4',
  importantPapers: '#6366F1',
  bankAccounts: '#8B5CF6',
  fixedDeposits: '#A855F7',
  insurance: '#EC4899',
  mutualFunds: '#F59E0B',
  stocks: '#10B981',
  crypto: '#3B82F6',
  websitesDomains: '#06B6D4',
  cloudStorage: '#6366F1',
  collectibles: '#F97316',
  otherPhysicalAssets: '#385DF6',
  businessOwnership: '#14B8A6',
};
const liabilityColors = {
  mortgage: '#F97316',
  'vehicle loan': '#3B82F6',
  loan: '#10B981',
  'credit card': '#EF4444',
  'personal debt': '#8B5CF6',
  'insurance premium': '#F59E0B',
  'tax payable': '#EC4899',
  overdraft: '#06B6D4',
  bnpl: '#6366F1'
};

// Period filter - commented out as not in API
// const periodOptions = [
//   { value: 'weekly', label: 'Weekly' },
//   { value: 'monthly', label: 'Monthly' },
//   { value: 'yearly', label: 'Yearly' }
// ];

// Type Filter - All, Assets, Liability
const typeOptions = [
  { value: 'all', label: 'All' },
  { value: 'assets', label: 'Assets' },
  { value: 'liability', label: 'Liability' }
];

// Digital/Physical sub-filter options (shown when Assets is selected)
const assetCategoryOptions = [
  { value: 'all', label: 'All' },
  { value: 'digital', label: 'Digital' },
  { value: 'physical', label: 'Physical' }
];

// Liability types from the system
const liabilityTypeOptions = [
  { value: 'all', label: 'All' },
  { value: 'mortgage', label: 'Mortgage' },
  { value: 'vehicle loan', label: 'Vehicle Loan' },
  { value: 'loan', label: 'Loan' },
  { value: 'credit card', label: 'Credit Card' },
  { value: 'personal debt', label: 'Personal Debt' },
  { value: 'insurance premium', label: 'Insurance Premium' },
  { value: 'tax payable', label: 'Tax Payable' },
  { value: 'overdraft', label: 'Overdraft' },
  { value: 'bnpl', label: 'Buy Now Pay Later' }
];

// Physical asset types
const physicalAssetTypes = [
  { value: 'all', label: 'All' },
  { value: 'homeProperty', label: 'Home Property' },
  { value: 'vehicles', label: 'Vehicles' },
  { value: 'preciousMetals', label: 'Precious Metals' },
  { value: 'electronics', label: 'Electronics' },
  { value: 'furnitureArt', label: 'Furniture & Art' },
  { value: 'cash', label: 'Cash' },
  { value: 'collectibles', label: 'Collectibles' },
  { value: 'otherPhysicalAssets', label: 'Other Physical Assets' }
];

// Digital asset types
const digitalAssetTypes = [
  { value: 'all', label: 'All' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'bankAccounts', label: 'Bank Accounts' },
  { value: 'mutualFunds', label: 'Mutual Funds' },
  { value: 'fixedDeposits', label: 'Fixed Deposit' },
  { value: 'stocks', label: 'Stocks' },
  { value: 'crypto', label: 'Crypto' },
  { value: 'businessOwnership', label: 'Business Ownership / Equity' },
  { value: 'importantPapers', label: 'Important Papers' }
];

const AssetDistributionChart = () => {
  // Open on assets so the chart shows asset allocation; liabilities stay available in the filter (ML-36)
  const [type, setType] = useState('assets');
  const [assetCategory, setAssetCategory] = useState('all');
  const [assetType, setAssetType] = useState('all');
  const [specificAssetType, setSpecificAssetType] = useState('all');
  const [chartData, setChartData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalValue, setTotalValue] = useState(0);
  const [activeIndex, setActiveIndex] = useState(null);
  const isMobile = useIsMobile();

  // Fetch chart data and available types from API
  useEffect(() => {
    setIsLoading(true);
    // Determine the API parameters based on selection
    let apiType = type;
    let category = 'all';
    let apiAssetType = assetType;
    let liabilityType = 'all';

    // When Assets is selected
    if (type === 'assets') {
      apiType = 'assets';
      // If a specific asset category (digital/physical) is selected, pass it as category
      if (assetCategory !== 'all') {
        category = assetCategory;
        // If a specific asset type is also selected, pass it as assetType
        apiAssetType = specificAssetType !== 'all' ? specificAssetType : 'all';
      } else {
        category = 'all';
        apiAssetType = 'all';
      }
    }
    // When Liability is selected
    else if (type === 'liability') {
      apiType = 'liabilities';
      // Pass specific liability type as liabilityType parameter
      liabilityType = assetType !== 'all' ? assetType : 'all';
      // Set assetType to 'all' when viewing liabilities
      apiAssetType = 'all';
      category = 'all';
    }
    // When All is selected
    else if (type === 'all') {
      apiType = 'all';
      category = 'all';
      apiAssetType = 'all';
    }

    api.get('/dashboard/charts/asset-distribution', {
      params: {
        assetType: apiAssetType,
        type: apiType,
        category,
        liabilityType
      },
      headers: getAuthHeaders()
    })
      .then(res => {
        // Prefer data array, else use assetDistribution/liabilityDistribution
        // let apiData = res.data?.data;
        // if (!Array.isArray(apiData) || apiData.length === 0) {
        //   apiData = [];
        //   if (Array.isArray(res.data?.assetDistribution)) {
        //     apiData = apiData.concat(res.data.assetDistribution.map(item => ({ ...item, isAsset: true })));
        //   }
        //   if (Array.isArray(res.data?.liabilityDistribution)) {
        //     apiData = apiData.concat(res.data.liabilityDistribution.map(item => ({ ...item, isAsset: false })));
        //   }
        // }
        let apiData = [];

        if (Array.isArray(res.data?.assetDistribution)) {
          apiData = res.data.assetDistribution.map(item => ({
            ...item,
            isAsset: true
          }));
        }

        // Also include liability distribution when viewing liabilities or all
        if (Array.isArray(res.data?.liabilityDistribution)) {
          apiData = apiData.concat(
            res.data.liabilityDistribution.map(item => ({
              ...item,
              isAsset: false
            }))
          );
        }

        // Collect all unique asset/liability types from API response, even if value/count is 0
        let assetTypes = [];
        let liabilityTypes = [];
        apiData.forEach(item => {
          if (item.assetType && !assetTypes.includes(item.assetType)) {
            assetTypes.push(item.assetType);
          }
          //   if (item.liabilityType && !liabilityTypes.includes(item.liabilityType)) {
          //     liabilityTypes.push(item.liabilityType);
          //   }
        });
        // If API provides assetDistribution or liabilityDistribution arrays, include their types too
        if (res.data?.assetDistribution) {
          res.data.assetDistribution.forEach(item => {
            if (item.assetType && !assetTypes.includes(item.assetType)) {
              assetTypes.push(item.assetType);
            }
          });
        }
        // if (res.data?.liabilityDistribution) {
        //   res.data.liabilityDistribution.forEach(item => {
        //     if (item.liabilityType && !liabilityTypes.includes(item.liabilityType)) {
        //       liabilityTypes.push(item.liabilityType);
        //     }
        //   });
        // }
        // Build dropdown options based on selected type - using static options for Digital/Physical assets
        let options = [];

        if (category === 'physicalAssets') {
          // Show only physical asset types
          const physicalTypes = [
            { value: 'homeProperty', label: 'Home Property' },
            { value: 'vehicles', label: 'Vehicles' },
            { value: 'preciousMetals', label: 'Precious Metals' },
            { value: 'electronics', label: 'Electronics' },
            { value: 'furnitureArt', label: 'Furniture & Art' },
            { value: 'cash', label: 'Cash' },
            { value: 'bankAccounts', label: 'Bank Accounts' },
            { value: 'fixedDeposits', label: 'Fixed Deposits' },
            { value: 'insurance', label: 'Insurance' },
            { value: 'collectibles', label: 'Collectibles' }
          ];
          options = [{ value: 'all', label: 'All' }, ...physicalTypes];
        } else if (category === 'digitalAssets') {
          // Show only digital asset types
          const digitalTypes = [
            { value: 'crypto', label: 'Crypto' },
            { value: 'websitesDomains', label: 'Websites & Domains' },
            { value: 'cloudStorage', label: 'Cloud Storage' },
            { value: 'importantPapers', label: 'Important Papers' },
            { value: 'mutualFunds', label: 'Mutual Funds' },
            { value: 'stocks', label: 'Stocks' }
          ];
          options = [{ value: 'all', label: 'All' }, ...digitalTypes];
        } else {
          // category === 'all', show all asset types combined
          options = [
            { value: 'all', label: 'All' }
          ];
        }

        // Use a local variable to store the options (not state)
        const assetTypeOptions = options;
        // Get the appropriate options to check against based on type
        let validOptions = assetTypeOptions;
        if (type === 'liability') {
          validOptions = liabilityTypeOptions;
        }
        // If current assetType is not in valid options, reset to 'all'
        if (!validOptions.find(opt => opt.value === assetType)) {
          setAssetType('all');
        }
        // Size segments by share of value (the API's percentage), not by how many items there are;
        // keep the count for the centre total and tooltip (ML-36)
        let mapped = apiData.map(item => {
          const isAsset = item.isAsset !== false && (!!item.assetType || !item.liabilityType);
          const key = item.assetType || item.liabilityType;
          return {
            name: key
              .replace(/([A-Z])/g, ' $1')
              .replace(/^./, str => str.toUpperCase()),
            value: typeof item.percentage === 'number' ? item.percentage : (typeof item.count === 'number' ? item.count : (item.value || 0)),
            share: typeof item.percentage === 'number' ? item.percentage : null,
            count: typeof item.count === 'number' ? item.count : null,
            color: isAsset ? assetColors[key] : liabilityColors[key] || '#888',
            type: isAsset ? 'asset' : 'liability',
            assetType: item.assetType || null,
            liabilityType: item.liabilityType || null
          };
        });

        // Filter for digital or physical assets if selected
        if (type === 'assets') {
          if (assetCategory === 'digital') {
            mapped = mapped.filter(item => item.type === 'asset' && ['insurance', 'bankAccounts', 'mutualFunds', 'fixedDeposits', 'stocks', 'crypto', 'businessOwnership', 'importantPapers'].includes(item.assetType));
          } else if (assetCategory === 'physical') {
            mapped = mapped.filter(item => item.type === 'asset' && !['insurance', 'bankAccounts', 'mutualFunds', 'fixedDeposits', 'stocks', 'crypto', 'businessOwnership', 'importantPapers'].includes(item.assetType));
          }

          // Filter for specific asset type if selected
          if (specificAssetType !== 'all') {
            mapped = mapped.filter(item => item.type === 'asset' && item.assetType === specificAssetType);
          }
        }

        // Filter for specific liability type if selected (when type === 'liability')
        if (type === 'liability' && assetType !== 'all') {
          console.log('Filtering for liability type:', assetType);
          console.log('Available liability types:', mapped.filter(i => i.type === 'liability').map(i => i.liabilityType));
          mapped = mapped.filter(item => item.type === 'liability' && item.liabilityType === assetType);
          console.log('Filtered liability data:', mapped);
        }
        setChartData(mapped);
        // Centre "Total" shows the number of items
        setTotalValue(mapped.reduce((sum, item) => sum + (item.count ?? item.value), 0));
      })
      .catch((error) => {
        console.error('Error fetching asset distribution:', error);
        // If the error is about invalid assetType, reset to 'all'
        if (error.response?.data?.message?.includes('must be one of')) {
          setAssetType('all');
        }
        setChartData([]);
        setTotalValue(0);
      })
      .finally(() => setIsLoading(false));
  }, [assetCategory, assetType, type, specificAssetType]);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const percentage = data.share !== null && data.share !== undefined ? data.share : ((data.value / totalValue) * 100).toFixed(1);
      return (
        <div className="custom-tooltip">
          <div className="tooltip-header">
            <div className="tooltip-indicator" style={{ backgroundColor: data.color }}></div>
            <p className="tooltip-name" style={{ margin: '0px' }}>{data.name}</p>
          </div>
          <p className="tooltip-value" style={{ margin: '4px 0px' }}>{data.count !== null && data.count !== undefined ? `${data.count} item${data.count === 1 ? '' : 's'}` : data.value.toLocaleString()}</p>
          <p className="tooltip-percentage" style={{ margin: '4px 0px' }}>{percentage}%</p>
        </div>
      );
    }
    return null;
  };

  const renderLegend = (props) => {
    const { payload } = props;
    // Mobile: dynamic columns (up to 3) + smaller widths to avoid horizontal scroll
    // Desktop/tablet: keep original 2 columns and larger minWidth
    const numCols = isMobile && chartData.length > 6 ? 3 : 2;
    const colSize = Math.ceil(chartData.length / numCols);
    const col1 = chartData.slice(0, colSize);
    const col2 = chartData.slice(colSize, colSize * 2);
    const col3 = chartData.slice(colSize * 2);

    const columns = [col1, col2, col3].filter((col) => col.length > 0);

    return (
      <div className="custom-legend">
        {columns.map((col, colIndex) => {
          const colOffset = columns
            .slice(0, colIndex)
            .reduce((sum, column) => sum + column.length, 0);

          return (
            <div
              key={`legend-col-${colIndex}`}
              className="legend-column"
              style={{ minWidth: isMobile ? '110px' : '140px' }}
            >
              {col.map((entry, index) => {
                const legendIndex = colOffset + index;

                return (
                  <div
                    key={`legend-${colIndex}-${index}`}
                    className={`legend-item ${activeIndex === legendIndex ? 'active' : ''}`}
                    onMouseEnter={() => setActiveIndex(legendIndex)}
                    onMouseLeave={() => setActiveIndex(null)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '2px 4px',
                      marginBottom: '2px',
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: entry.color,
                        marginRight: 6,
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        fontWeight: 500,
                        marginRight: 4,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                      className="legend-name subtitle2"
                    >
                      {entry.name}
                    </span>
                    <span
                      style={{ color: '#444', fontWeight: 600 }}
                      className="legend-value subtitle2"
                    >
                      {entry.share !== null && entry.share !== undefined ? `${entry.share}%` : (entry.value > 0 ? `${entry.value.toLocaleString()}` : '')}
                    </span>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  };

  // Get second filter options based on type selection
  const getSecondFilterOptions = () => {
    if (type === 'all') {
      return [{ value: 'all', label: 'All' }];
    } else if (type === 'assets') {
      return assetCategoryOptions;
    } else if (type === 'liability') {
      // Hide liability specific selection dropdown - return only 'All'
      return [{ value: 'all', label: 'All' }];
    }
    return [{ value: 'all', label: 'All' }];
  };

  // Get the second filter value based on type
  const getSecondFilterValue = () => {
    const options = getSecondFilterOptions();
    if (type === 'assets') {
      return options.find(opt => opt.value === assetCategory) || options[0];
    } else if (type === 'liability') {
      return options.find(opt => opt.value === assetType) || options[0];
    }
    return options[0];
  };

  // Handle second filter change
  const handleSecondFilterChange = (option) => {
    if (type === 'assets') {
      setAssetCategory(option.value);
      // Reset specific asset type when category changes
      setSpecificAssetType('all');
    } else if (type === 'liability') {
      setAssetType(option.value);
    }
  };

  // Static asset type options (will be filtered based on assetCategory)
  const getAssetTypeOptions = () => {
    if (assetCategory === 'physical') {
      return [
        { value: 'all', label: 'All' },
        { value: 'homeProperty', label: 'Home Property' },
        { value: 'vehicles', label: 'Vehicles' },
        { value: 'preciousMetals', label: 'Precious Metals' },
        { value: 'electronics', label: 'Electronics' },
        { value: 'furnitureArt', label: 'Furniture & Art' },
        { value: 'cash', label: 'Cash' },
        { value: 'bankAccounts', label: 'Bank Accounts' },
        { value: 'fixedDeposits', label: 'Fixed Deposits' },
        { value: 'insurance', label: 'Insurance' },
        { value: 'collectibles', label: 'Collectibles' }
      ];
    } else if (assetCategory === 'digital') {
      return [
        { value: 'all', label: 'All' },
        { value: 'crypto', label: 'Crypto' },
        { value: 'websitesDomains', label: 'Websites & Domains' },
        { value: 'cloudStorage', label: 'Cloud Storage' },
        { value: 'importantPapers', label: 'Important Papers' },
        { value: 'mutualFunds', label: 'Mutual Funds' },
        { value: 'stocks', label: 'Stocks' }
      ];
    }
    return [{ value: 'all', label: 'All' }];
  };

  return (
    <div className="chart-container">
      {/* ...copy your style and layout from your static version here... */}
      <div className="chart-header" style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? '8px' : '12px' }}>
        <div className="dash-card-header-deatail"><h6 className="">Distribution<InfoTooltip content="Asset distribution shows how your assets are allocated across different categories." /></h6></div>
        <div className="filters-container" style={{ width: isMobile ? '100%' : 'auto' }}>
          <div className="filter-group" style={{ display: 'flex', justifyContent: isMobile ? 'flex-start' : 'space-between', gap: '10px', flexWrap: 'wrap' }}>
            {/* Period filter - commented out as not in API */}
            {/*
            <Select
              value={periodOptions.find(opt => opt.value === period) || periodOptions[0]}
              onChange={(option) => setPeriod(option.value)}
              options={periodOptions}
              className="react-select-container"
              classNamePrefix="react-select"
              isSearchable={false}
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: '32px',
                  fontSize: '12px',
                  borderRadius: '6px',
                  border: '1px solid #e2e8f0',
                  minWidth: '100px',
                }),
                option: (base) => ({
                  ...base,
                  fontSize: '12px',
                }),
              }}
            />
            */}
            {/* Type Filter: All, Assets, Liability */}
            <Select
              value={typeOptions.find(opt => opt.value === type) || typeOptions[0]}
              onChange={(option) => {
                setType(option.value);
                // Reset filters when type changes
                setAssetType('all');
                setAssetCategory('all');
                setSpecificAssetType('all');
              }}
              options={typeOptions}
              className="react-select-container"
              classNamePrefix="react-select"
              isSearchable={false}
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: '36px',
                  fontSize: '14px',
                  fontWeight: 600,
                  borderRadius: '8px',
                  border: '1px solid var(--border-color, #CBD5E1)',
                  backgroundColor: 'var(--bg-surface, #FFFFFF)',
                  minWidth: '110px',
                }),
                singleValue: (base) => ({
                  ...base,
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--heading-color, #0F172A)',
                }),
                option: (base) => ({
                  ...base,
                  fontSize: '14px',
                  fontWeight: 500,
                }),
              }}
            />
            {/* Second Filter - Only visible for Assets type (Digital/Physical selection) */}
            {/* Hidden when Liability is selected */}
            {type === 'assets' && (
              <Select
                value={getSecondFilterValue()}
                onChange={handleSecondFilterChange}
                options={getSecondFilterOptions()}
                className="react-select-container"
                classNamePrefix="react-select"
                isSearchable={false}
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: '36px',
                    fontSize: '14px',
                    fontWeight: 600,
                    borderRadius: '8px',
                    border: '1px solid var(--border-color, #CBD5E1)',
                    backgroundColor: 'var(--bg-surface, #FFFFFF)',
                    minWidth: '150px',
                  }),
                  singleValue: (base) => ({
                    ...base,
                    fontSize: '14px',
                    fontWeight: 600,
                    color: 'var(--heading-color, #0F172A)',
                  }),
                  option: (base) => ({
                    ...base,
                    fontSize: '14px',
                    fontWeight: 500,
                  }),
                }}
              />
            )}
            {/* Third Filter - Specific asset types when Assets + Digital/Physical is selected */}
            {/* {type === 'assets' && assetCategory !== 'all' && (
              <Select
                value={assetCategory === 'physical' 
                  ? physicalAssetTypes.find(opt => opt.value === specificAssetType) || physicalAssetTypes[0]
                  : digitalAssetTypes.find(opt => opt.value === specificAssetType) || digitalAssetTypes[0]
                }
                onChange={(option) => setSpecificAssetType(option.value)}
                options={assetCategory === 'physical' ? physicalAssetTypes : digitalAssetTypes}
                className="react-select-container"
                classNamePrefix="react-select"
                isSearchable={false}
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: '32px',
                    fontSize: '12px',
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0',
                    minWidth: '140px',
                  }),
                  option: (base) => ({
                    ...base,
                    fontSize: '12px',
                  }),
                }}
              />
            )} */}
          </div>
        </div>
      </div>
      {isLoading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
        </div>
      )}
      <div className="chart-content">
        {/* Check if a specific type is selected but there's no data */}
        {(() => {
          const isSpecificTypeSelected =
            (type === 'assets' && specificAssetType !== 'all') ||
            (type === 'liability' && assetType !== 'all');

          // If specific type selected but no data, create placeholder
          if (chartData.length === 0 && !isLoading && isSpecificTypeSelected) {
            const selectedTypeName = type === 'assets'
              ? (specificAssetType === 'businessOwnership' ? 'Business Ownership / Equity' : specificAssetType)
              : assetType;
            // Use a minimum value of 1 to ensure the pie chart renders
            const placeholderData = [{
              name: selectedTypeName,
              value: 1, // Use 1 for display, will show as 0
              displayValue: 0, // Actual value to display
              color: '#CCCCCC', // Gray color
              type: type === 'assets' ? 'asset' : 'liability',
              assetType: type === 'assets' ? specificAssetType : null,
              liabilityType: type === 'liability' ? assetType : null
            }];

            return (
              <div className="donut-container">
                <div className="chart-wrapper">
                  <PieChart width={200} height={200}>
                    <Pie
                      data={placeholderData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={75}
                      paddingAngle={2}
                      dataKey="value"
                      cornerRadius={5}
                      animationBegin={0}
                      animationDuration={800}
                    >
                      {placeholderData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={entry.color}
                        />
                      ))}
                    </Pie>
                    <text x="100" y="95" textAnchor="middle" fontSize="14" fontWeight="500" fill="#666">Total</text>
                    <text x="100" y="115" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#222">0</text>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </div>
                <div style={{ textAlign: 'center', padding: '10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', background: '#CCCCCC' }}></span>
                    <span style={{ fontWeight: 500 }}>{selectedTypeName}</span>
                    <span style={{ fontWeight: 600, color: '#444' }}>0</span>
                  </div>
                </div>
              </div>
            );
          }

          // Normal rendering when there's data or no specific type selected
          // Show gray placeholder pie chart when there's no data
          if (chartData.length === 0) {
            return (
              <div className="donut-container">
                <div className="chart-wrapper">
                  <PieChart width={200} height={200}>
                    <Pie
                      data={[{ name: 'No Data', value: 1, color: '#e0e0e0' }]}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={75}
                      paddingAngle={2}
                      dataKey="value"
                      cornerRadius={5}
                      label={({ cx, cy }) => (
                        <g>
                          <text
                            x={cx}
                            y={cy - 8}
                            textAnchor="middle"
                            fontSize="14"
                            fontWeight="500"
                            fill="#666"
                          >
                            Total
                          </text>
                          <text
                            x={cx}
                            y={cy + 14}
                            textAnchor="middle"
                            fontSize="16"
                            fontWeight="bold"
                            fill="#222"
                          >
                            0
                          </text>
                        </g>
                      )}
                      labelLine={false}
                    >
                      <Cell fill="#e0e0e0" />
                    </Pie>
                  </PieChart>
                </div>
              </div>
            );
          }

          return (
            <div className="donut-container">
              <div className="chart-wrapper">
                <PieChart width={200} height={200}>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={75}
                    paddingAngle={2}
                    dataKey="value"
                    cornerRadius={5}
                    animationBegin={0}
                    animationDuration={800}
                    onMouseEnter={(_, index) => setActiveIndex(index)}
                    onMouseLeave={() => setActiveIndex(null)}
                    label={({ cx, cy }) => (
                      <g>
                        <text
                          x={cx}
                          y={cy - 8}
                          textAnchor="middle"
                          fontSize="14"
                          fontWeight="500"
                          fill="#666"
                        >
                          Total
                        </text>
                        {totalValue > 0 && (
                          <text
                            x={cx}
                            y={cy + 14}
                            textAnchor="middle"
                            fontSize="16"
                            fontWeight="bold"
                            fill="#222"
                          >
                            {`${totalValue.toLocaleString()}`}
                          </text>
                        )}
                      </g>
                    )}
                    labelLine={false}
                  >
                    {chartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.color}
                        opacity={activeIndex === null || activeIndex === index ? 1 : 0.3}
                        style={{
                          transition: 'all 0.3s ease',
                          filter: activeIndex === index ? 'brightness(1.2)' : 'none'
                        }}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
                {/* Total Value now shown inside the chart */}
              </div>
              <div className="legend-wrapper" style={{ width: isMobile ? '100%' : 'auto', flex: isMobile ? 'none' : '1', minWidth: isMobile ? 'auto' : '260px' }}>
                {renderLegend({ payload: chartData.map((item, index) => ({ value: item.name, color: item.color })) })}
              </div>
            </div>
          );
        })()}
      </div>

    </div>
  );
};

export default AssetDistributionChart;

